/* Implements Warm Academic theme (Option 2) for Homepage / Dashboard experience (Khan Academy & Coursera feel) */
import React, { useState, useEffect } from 'react';
import { 
  BookOpen, GraduationCap, Layers, ArrowRight, Check, Award, 
  Clock, Activity, Users, ClipboardList, CheckCircle2, ChevronRight, 
  FileText, Sparkles, HeartPulse, Brain, Beaker, Headphones, Flame, 
  Trophy, Target, HelpCircle, ShieldCheck, Compass, Play, Zap, ListChecks
} from 'lucide-react';
import { useAuth } from './AuthBridge';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { StorageService } from '../services/storageService';
import { AdUnit } from './AdUnit';
import { ContinueLastQuizCard } from './ContinueLastQuizCard';

interface WarmAcademicDashboardProps {
  onNavigate: (page: string) => void;
  standingData?: any;
}

export const WarmAcademicDashboard: React.FC<WarmAcademicDashboardProps> = ({ onNavigate, standingData }) => {
  const { user, activeCert } = useAuth();
  const tokens = useThemeTokens();
  const [facultyMembers, setFacultyMembers] = useState<any[]>([]);

  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        const data = await StorageService.getFaculty();
        if (Array.isArray(data) && data.length > 0) {
          setFacultyMembers(data);
        }
      } catch (err) {
        console.error("Failed to load faculty for dashboard:", err);
      }
    };
    fetchFaculty();
  }, []);

  const getStateFullName = (st?: string) => {
    if (!st || st === 'Global' || st === 'Universal') return '';
    const stateMap: Record<string, string> = {
      'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
      'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
      'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
      'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
      'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
      'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
      'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
      'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
      'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
      'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
    };
    return stateMap[st.toUpperCase()] || st;
  };

  const certName = activeCert?.shortName || 'CNA';
  const certFullName = activeCert?.name || 'Certified Nursing Assistant';
  const stateFullName = getStateFullName(user?.state);
  const isStateSpecific = Boolean(stateFullName);
  const fullCertHeading = isStateSpecific ? `${stateFullName} ${certFullName}` : certFullName;

  // Dynamic Exam Readiness Estimate calculation from actual learning progress
  const calculateReadinessScore = () => {
    if (typeof standingData?.forecastScore === 'number' && standingData.forecastScore > 0) {
      return Math.min(100, Math.max(0, Math.round(standingData.forecastScore)));
    }

    const scores: number[] = [];

    // Practice exam history
    if (Array.isArray(user?.history) && user.history.length > 0) {
      user.history.forEach((h: any) => {
        if (typeof h.score === 'number') scores.push(h.score);
      });
    }

    // Practice quiz average score
    if (typeof user?.stats?.averageScore === 'number' && user.stats.averageScore > 0) {
      scores.push(user.stats.averageScore);
    }

    if (scores.length > 0) {
      const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
      const quizzesTaken = user?.stats?.quizzesTaken || 0;
      const activityBonus = Math.min(10, Math.floor(quizzesTaken / 2));
      return Math.min(99, Math.max(5, avg + activityBonus));
    }

    // Dynamic score based on user points / activity if no history scores yet
    const points = user?.stats?.points || 0;
    if (points > 0) {
      return Math.min(85, Math.max(10, Math.round(points / 25)));
    }

    return 0; // 0% readiness when student hasn't started practice yet
  };

  // Metrics calculations
  const totalExams = user?.history?.length || 0;
  const highestScore = user?.history && user.history.length > 0 
    ? Math.max(...user.history.map((h: any) => h.score || 0)) 
    : 0;
  const readinessPercent = calculateReadinessScore();
  const rawStudyHours = user?.stats?.studyTimeMinutes ? (user.stats.studyTimeMinutes / 60) : (user?.stats?.studyHours || 0);
  const studyHours = Number.isInteger(rawStudyHours) ? rawStudyHours : Number(rawStudyHours).toFixed(2);
  const currentStreak = user?.stats?.streakDays || user?.stats?.streakCount || 0;
  const questionsAnswered = user?.stats?.totalQuestionsAnswered || (user?.stats?.quizzesTaken ? user.stats.quizzesTaken * 10 : 0);
  const badgeCount = user?.stats?.points ? Math.floor(user.stats.points / 150) : (user?.stats?.badges?.length || 0);

  const pathways = [
    {
      id: 'study-guide',
      title: 'Clinical Theory & Manual',
      description: 'Comprehensive core textbook chapters, procedure steps, and state board theory guidelines.',
      progress: Math.min(100, Math.round(((user?.stats?.quizzesTaken || 0) * 8) + (user?.history?.length ? 15 : 0))),
      icon: BookOpen,
      category: 'Foundation',
    },
    {
      id: 'practice',
      title: 'Practice Quiz & Drills',
      description: 'Categorized practice question sets with instant rationale explanations and learning feedback.',
      progress: Math.min(100, Math.round((user?.stats?.quizzesTaken || 0) * 12)),
      icon: ListChecks,
      category: 'Practice',
    },
    {
      id: 'exam',
      title: 'Practice Exam',
      description: 'Full-length practice exams styled after the actual state certification exam format.',
      progress: Math.min(100, Math.round((user?.history?.length || 0) * 20)),
      icon: ClipboardList,
      category: 'Testing',
    },
    {
      id: 'simulator_lab',
      title: 'Board Simulation',
      description: 'Timed state board exam environment with state-specific passing thresholds and scoring.',
      progress: readinessPercent,
      icon: GraduationCap,
      category: 'Testing',
    },
    {
      id: 'skills',
      title: 'Skill Lab',
      description: 'Step-by-step physical skill checklists, critical indicator steps, and evaluator notes.',
      progress: user?.stats?.skillsMastered ? Math.min(100, user.stats.skillsMastered * 10) : 0,
      icon: HeartPulse,
      category: 'Clinical',
    },
    {
      id: 'scenarios',
      title: 'Scenario Challenges',
      description: 'Interactive real-world patient care dilemmas and ethical decision-making scenarios.',
      progress: user?.history ? Math.min(100, user.history.filter((h: any) => h.examMode?.includes('Scenario')).length * 25) : 0,
      icon: Brain,
      category: 'Clinical',
    },
    {
      id: 'adaptive_decks',
      title: 'Adaptive Study Decks',
      description: 'Smart spaced-repetition algorithm targeting your specific weak areas automatically.',
      progress: user?.flashcardSRS ? Math.min(100, Object.keys(user.flashcardSRS).length * 5) : 0,
      icon: Layers,
      category: 'Adaptive',
    },
    {
      id: 'topic_decks',
      title: 'Topic Study Decks',
      description: 'Focused flashcards grouped by state curriculum domain and clinical topic.',
      progress: user?.flashcardSRS ? Math.min(100, Object.keys(user.flashcardSRS).length * 8) : 0,
      icon: Target,
      category: 'Adaptive',
    },
    {
      id: 'flashcards',
      title: 'Flashcards',
      description: 'Rapid terminology review and medical vocabulary builder for nursing assistants.',
      progress: user?.flashcardSRS ? Math.min(100, Object.keys(user.flashcardSRS).length * 10) : 0,
      icon: Sparkles,
      category: 'Review',
    },
    {
      id: 'podcasts',
      title: 'Audio Learning & Lectures',
      description: 'Listen to guided audio lessons and nursing educator explanations on the go.',
      progress: user?.stats?.podcastsListened ? Math.min(100, user.stats.podcastsListened * 20) : 0,
      icon: Headphones,
      category: 'Review',
    },
  ];

  const defaultFaculty = [
    {
      _id: '1',
      name: 'Dr. Eleanor Vance, DNP, RN',
      title: 'Lead Nursing Educator',
      credentials: 'DNP, RN, CNE',
      bio: '20+ years in nursing education and clinical instruction. Specializes in long-term care & state board preparation.',
      specialties: ['Long-Term Care', 'State Board Prep'],
    },
    {
      _id: '2',
      name: 'Prof. Marcus Sterling, MSN, RN',
      title: 'Skills & Evaluation Director',
      credentials: 'MSN, RN, CNA Evaluator',
      bio: 'Former state board clinical skills evaluator. Passionate about helping adult learners pass their physical skills exam.',
      specialties: ['Physical Skills Evaluation', 'Clinical Safety'],
    },
    {
      _id: '3',
      name: 'Sarah Jenkins, BSN, RN',
      title: 'Clinical Scenario Specialist',
      credentials: 'BSN, RN, CRRN',
      bio: 'Rehabilitation nursing expert focusing on patient safety, infection control, and resident rights education.',
      specialties: ['Infection Control', 'Resident Rights'],
    },
  ];

  const displayFaculty = facultyMembers.length > 0 ? facultyMembers : defaultFaculty;

  return (
    <div className="space-y-8 animate-fade-in max-w-7xl mx-auto text-[#2D3748] dark:text-slate-100 pb-20 font-sans w-full">
      
      {/* Top Header Bar: Breadcrumb & Curriculum Status */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-[#E3DFD5] dark:border-slate-800 shadow-xs">
        <div className="flex items-center gap-2.5 text-xs font-semibold text-[#4A5568] dark:text-slate-300">
          <div>
            <span className="text-slate-900 dark:text-white font-bold text-sm block leading-none">{certName} Learning Dashboard</span>
            <span className="text-[10px] text-slate-400 font-medium">Board Exam Preparation & Curriculum Standards</span>
          </div>
          <ChevronRight size={14} className="text-slate-300 mx-1 hidden sm:inline" />
          <span className="hidden sm:inline-block px-2.5 py-0.5 bg-[#E6F4F1] text-[#0D6E6E] rounded-full text-[10px] font-bold">
            {isStateSpecific ? `${stateFullName} Curriculum` : 'Universal Curriculum'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#E6F4F1] text-[#0D6E6E] rounded-full text-xs font-bold border border-[#0D6E6E]/20">
            <CheckCircle2 size={14} className="text-[#0D6E6E]" />
            <span>{isStateSpecific ? `${stateFullName} Board Standard Verified` : 'Universal Board Standard Verified'}</span>
          </span>
        </div>
      </div>

      {/* Continue In-Progress Quiz / Exam Banner */}
      <ContinueLastQuizCard onNavigate={onNavigate} />

      {/* Hero Welcome Section (Warm & Encouraging - Khan Academy / Coursera feel) + Right Ad */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
        <div className="xl:col-span-8 bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-10 border border-[#E3DFD5] dark:border-slate-800 shadow-xs relative overflow-hidden flex flex-col justify-between">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E6F4F1] text-[#0D6E6E] text-xs font-bold rounded-full border border-[#0D6E6E]/20">
                <Sparkles size={13} className="text-[#E05638]" />
                <span>Welcome Back to Your Learning Space</span>
              </div>

              <div className="space-y-2">
                <h1 className="text-2xl md:text-4xl font-sans font-bold text-[#2D3748] dark:text-slate-100 tracking-tight leading-snug">
                  Hello, <span className="text-[#0D6E6E]">{user?.name?.split(' ')[0] || 'Learner'}</span>! 👋
                </h1>
                <p className="text-[#4A5568] dark:text-slate-300 text-sm md:text-base leading-relaxed font-normal max-w-2xl">
                  You're making steady progress toward your <strong className="text-[#2D3748] dark:text-slate-100">{fullCertHeading}</strong> license. Step by step, every study session builds real confidence!
                </p>
              </div>

              {/* Motivational Readiness Badge & Encouraging Message */}
              <div className="p-4 bg-[#F8F7F3] dark:bg-slate-800/80 rounded-2xl border border-[#E3DFD5] dark:border-slate-700/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#0D6E6E] text-white flex items-center justify-center font-bold text-lg shrink-0 shadow-xs">
                    {readinessPercent}%
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#2D3748] dark:text-slate-200 uppercase tracking-wider">Exam Readiness Estimate</h4>
                    <p className="text-xs text-[#4A5568] dark:text-slate-400 font-medium mt-0.5">
                      {readinessPercent >= 75 
                        ? 'Outstanding! You are well prepared for your board exam.' 
                        : readinessPercent > 0 
                        ? 'Great effort! Keep practicing your weak categories to reach 75%+ readiness.' 
                        : 'Take your first diagnostic practice quiz or exam to calculate your readiness estimate.'}
                    </p>
                  </div>
                </div>

                <button 
                  onClick={() => onNavigate('exam')}
                  className="px-5 py-2.5 bg-[#E05638] hover:bg-[#C8472B] text-white font-bold text-xs rounded-2xl shadow-xs transition-all active:scale-95 shrink-0 flex items-center gap-1.5"
                >
                  <span>Continue Exam Practice</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>

            {/* Right Hero Overview Card */}
            <div className="lg:col-span-5 bg-[#E6F4F1] dark:bg-slate-800/50 p-6 rounded-2xl border border-[#0D6E6E]/20 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-[#0D6E6E]/20">
                <span className="text-xs font-bold text-[#0D6E6E] dark:text-teal-400 uppercase tracking-wider">Today's Focus</span>
                <span className="text-[10px] font-bold px-2 py-0.5 bg-[#0D6E6E] text-white rounded-full">Suggested</span>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-[#0D6E6E] dark:text-teal-400 shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-xs font-bold text-[#2D3748] dark:text-slate-200">Patient Safety & Infection Control</h5>
                    <p className="text-[11px] text-[#4A5568] dark:text-slate-400 font-medium mt-0.5">Review hand hygiene procedures and PPE protocol guidelines.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock size={18} className="text-[#E05638] shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-xs font-bold text-[#2D3748] dark:text-slate-200">20-Minute Practice Quiz</h5>
                    <p className="text-[11px] text-[#4A5568] dark:text-slate-400 font-medium mt-0.5">Quick 10-question drill with instant rationale explanations.</p>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => onNavigate('practice')}
                className="w-full py-2.5 bg-[#0D6E6E] hover:bg-[#0B5C5C] text-white font-bold text-xs rounded-xl shadow-xs transition-all text-center block"
              >
                Start Recommended Session
              </button>
            </div>

          </div>
        </div>

        {/* Dedicated Right Column for Ad Unit outside hero banner */}
        <div className="xl:col-span-4 flex flex-col justify-start">
          <AdUnit placement="dashboard_welcome_right" />
        </div>
      </div>

      {/* Study Pathways Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[#2D3748] dark:text-slate-100 tracking-tight">
              Study Pathways & Learning Modules
            </h2>
            <p className="text-xs md:text-sm text-[#4A5568] dark:text-slate-400 font-medium">
              Choose your preferred learning format. All modules align directly with state certification standards.
            </p>
          </div>
          <span className="hidden sm:inline-block px-3 py-1 bg-[#E6F4F1] text-[#0D6E6E] text-xs font-bold rounded-full border border-[#0D6E6E]/20">
            10 Modules Available
          </span>
        </div>

        {/* Study Pathways Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-3">
          {pathways.map((item) => {
            const IconComp = item.icon;
            return (
              <div 
                key={item.id}
                className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-[#E3DFD5] dark:border-slate-800 shadow-xs hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col justify-between group"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-[#E6F4F1] dark:bg-slate-800 text-[#0D6E6E] dark:text-teal-400 flex items-center justify-center font-bold shadow-xs group-hover:bg-[#0D6E6E] group-hover:text-white transition-colors">
                      <IconComp size={22} />
                    </div>
                    <span className="px-2.5 py-1 bg-[#F8F7F3] dark:bg-slate-800 text-[#4A5568] dark:text-slate-300 text-[10px] font-bold rounded-full border border-[#E3DFD5] dark:border-slate-700">
                      {item.category}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    <h3 className="text-base font-bold text-[#2D3748] dark:text-slate-100 group-hover:text-[#0D6E6E] dark:group-hover:text-teal-400 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-[#4A5568] dark:text-slate-400 font-normal leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 pt-5 mt-4 border-t border-[#E3DFD5]/60 dark:border-slate-800/80">
                  <div className="space-y-1">
                    <div className="flex justify-between text-[11px] font-bold text-[#4A5568] dark:text-slate-400">
                      <span>Module Progress</span>
                      <span className="text-[#0D6E6E] dark:text-teal-400">{item.progress}%</span>
                    </div>
                    <div className="h-2 w-full bg-[#F8F7F3] dark:bg-slate-800 rounded-full overflow-hidden border border-[#E3DFD5]/50 dark:border-slate-700/50">
                      <div 
                        className="h-full bg-[#0D6E6E] dark:bg-teal-500 rounded-full transition-all duration-500" 
                        style={{ width: `${item.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <button 
                    onClick={() => onNavigate(item.id)}
                    className="w-full py-2.5 bg-[#E05638] hover:bg-[#C8472B] text-white font-bold text-xs rounded-2xl shadow-xs transition-all active:scale-95 flex items-center justify-center gap-1.5"
                  >
                    <span>Continue Learning</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Faculty Spotlight Section */}
      <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-[#E3DFD5] dark:border-slate-800 shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#E3DFD5] dark:border-slate-800 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0D6E6E] dark:text-teal-400 uppercase tracking-wider mb-1">
                <Users size={14} />
                <span>Expert Faculty Instruction</span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-[#2D3748] dark:text-slate-100 tracking-tight">
                Meet the faculty behind your questions
              </h2>
            </div>
            <button
              onClick={() => onNavigate('faculty')}
              className="px-4 py-2 bg-[#0D6E6E] hover:bg-[#095252] text-white rounded-xl text-xs font-bold transition-all shadow-xs shrink-0 self-start sm:self-auto cursor-pointer"
            >
              Meet Board Faculty &rarr;
            </button>
          </div>
          <p className="text-xs text-[#4A5568] dark:text-slate-400 font-medium max-w-md">
            All study guides, practice items, and clinical skills checklists are created and verified by our licensed nursing faculty.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {displayFaculty.map((faculty, idx) => (
            <div key={faculty._id || idx} className="bg-[#F8F7F3] dark:bg-slate-800/60 p-5 rounded-2xl border border-[#E3DFD5] dark:border-slate-700/80 space-y-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {faculty.avatarUrl ? (
                    <img 
                      src={faculty.avatarUrl} 
                      alt={faculty.name} 
                      className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0 border border-[#0D6E6E]/20"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-2xl bg-[#0D6E6E] text-white font-bold text-sm flex items-center justify-center shadow-xs shrink-0">
                      {faculty.name ? faculty.name.charAt(0) : 'F'}
                    </div>
                  )}
                  <div>
                    <h4 className="text-sm font-bold text-[#2D3748] dark:text-slate-100 leading-tight">
                      {faculty.name}
                    </h4>
                    {faculty.credentials && (
                      <span className="inline-block px-2 py-0.5 bg-[#E6F4F1] dark:bg-slate-700 text-[#0D6E6E] dark:text-teal-300 rounded-md text-[10px] font-bold mt-1">
                        {faculty.credentials}
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  {(faculty.title || faculty.role) && (
                    <p className="text-xs font-bold text-[#0D6E6E] dark:text-teal-400">
                      {faculty.title || faculty.role}
                    </p>
                  )}
                  <p className="text-xs text-[#4A5568] dark:text-slate-300 font-medium leading-relaxed line-clamp-3">
                    "{faculty.bio}"
                  </p>
                </div>

                {Array.isArray(faculty.specialties) && faculty.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {faculty.specialties.map((spec: string, i: number) => (
                      <span key={i} className="px-2 py-0.5 bg-white dark:bg-slate-900 text-[#4A5568] dark:text-slate-300 border border-[#E3DFD5] dark:border-slate-700 rounded text-[9px] font-bold">
                        {spec}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-[#E3DFD5] dark:border-slate-700/80 flex items-center justify-between text-[11px] font-bold text-emerald-700 dark:text-emerald-400">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck size={14} />
                  <span>Verified Faculty Educator</span>
                </span>
                <button onClick={() => onNavigate('faculty')} className="text-[#0D6E6E] hover:underline text-[10px] font-bold">
                  View Profile &rarr;
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Student Statistics (Visually Secondary) */}
      <div className="bg-[#F8F7F3] dark:bg-slate-900/60 rounded-3xl p-6 md:p-8 border border-[#E3DFD5] dark:border-slate-800 space-y-4">
        <div className="flex items-center gap-2 text-[#4A5568] dark:text-slate-400 border-b border-[#E3DFD5] dark:border-slate-800 pb-3">
          <Activity size={16} className="text-[#0D6E6E]" />
          <h3 className="text-xs font-bold tracking-wider uppercase">Your Learning Activity Summary</h3>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-[#E3DFD5] dark:border-slate-700 text-center space-y-1">
            <span className="text-[10px] font-bold text-[#4A5568] dark:text-slate-400 uppercase tracking-wider block">Study Streak</span>
            <div className="text-xl font-bold text-[#E05638] flex items-center justify-center gap-1">
              <Flame size={18} fill="currentColor" />
              <span>{currentStreak} Days</span>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-[#E3DFD5] dark:border-slate-700 text-center space-y-1">
            <span className="text-[10px] font-bold text-[#4A5568] dark:text-slate-400 uppercase tracking-wider block">Earned Badges</span>
            <div className="text-xl font-bold text-[#0D6E6E] dark:text-teal-400 flex items-center justify-center gap-1">
              <Trophy size={18} />
              <span>{badgeCount} Badges</span>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-[#E3DFD5] dark:border-slate-700 text-center space-y-1">
            <span className="text-[10px] font-bold text-[#4A5568] dark:text-slate-400 uppercase tracking-wider block">Exams Completed</span>
            <div className="text-xl font-bold text-[#2D3748] dark:text-slate-100">
              {totalExams} Exams
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-[#E3DFD5] dark:border-slate-700 text-center space-y-1">
            <span className="text-[10px] font-bold text-[#4A5568] dark:text-slate-400 uppercase tracking-wider block">Highest Score</span>
            <div className="text-xl font-bold text-[#0D6E6E] dark:text-teal-400">
              {highestScore > 0 ? `${highestScore}%` : '—'}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-[#E3DFD5] dark:border-slate-700 text-center space-y-1">
            <span className="text-[10px] font-bold text-[#4A5568] dark:text-slate-400 uppercase tracking-wider block">Qs Answered</span>
            <div className="text-xl font-bold text-[#2D3748] dark:text-slate-100">
              {questionsAnswered}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-[#E3DFD5] dark:border-slate-700 text-center space-y-1">
            <span className="text-[10px] font-bold text-[#4A5568] dark:text-slate-400 uppercase tracking-wider block">Study Hours</span>
            <div className="text-xl font-bold text-[#2D3748] dark:text-slate-100">
              {studyHours}h
            </div>
          </div>
        </div>
      </div>

      <div className="w-full pt-4">
        <AdUnit placement="dashboard_bottom" />
      </div>

    </div>
  );
};
